import {TrackingPageTypeWithoutId} from '@aboutyou/legacy-tracking-sdk';
import React from 'react';
import styled from 'styled-components';
import {specialSlugs} from '../../../localizedUrls/specialSlugs';
import {createHelpCenterSectionUrl} from '../../../pages/HelpCenter/routes';
import {
  HelpCenterArticlePageData,
  HelpCenterPageCommonProps,
  HelpCenterSection,
} from '../../../pages/HelpCenter/types';
import {SharedPageTrackingContextProvider} from '../../../tracking/context/PageTrackingContext';
import {getPageContextProps} from '../../../tracking/hooks/trackingHelpers';
import BreadCrumbs from '../../Navigation/BreadCrumbs/BreadCrumbs';
import {bordersColor, primaryColor} from '../../constants/colors';
import {
  mediaMaxTablet,
  minTabletOnlyCss,
  mobileOnlyCss,
} from '../../constants/customMedia';
import {gridGap, gridGapPx} from '../../constants/misc';
import {fsXL, fwHeavy, lhM} from '../../constants/typography';
import {LoadingContainer} from '../../utilityComponents/Loading/Loading';
import {CloudflareTurnstileType} from '../ContactPage/ContactForm';
import {FormDataRaw} from '../ContactPage/types';
import HelpdeskInnerPage from '../HelpdeskInnerPage/HelpdeskInnerPage';
import {useContactFormState} from '../hooks/useContactFormState';
import {ArticleContent} from './ArticleContent';
import ArticleUserFeedback from './ArticleUserFeedback';
import RelatedArticles from './RelatedArticles';

interface Props {
  locale: HelpCenterPageCommonProps['locale'];
  sections: HelpCenterPageCommonProps['sections'];
  translations: HelpCenterPageCommonProps['translations'];
  endpoints: HelpCenterPageCommonProps['endpoints'];
  activeSectionId: HelpCenterArticlePageData['activeSectionId'];
  relatedArticles: HelpCenterArticlePageData['relatedArticles'];
  activeArticle: HelpCenterArticlePageData['activeArticle'];
  isShowOldHelpCenterEnabled?: boolean;
  CloudflareTurnstile?: CloudflareTurnstileType;
  isDesktop?: boolean;
}

const ArticleContentWrapper = styled.div`
  @media ${mediaMaxTablet} {
    padding: 0 ${gridGapPx} ${gridGap * 2}px;
    border-bottom: 1px solid ${bordersColor};
  }
`;
const Title = styled.h1`
  font-size: ${fsXL}px;
  ${fwHeavy};
  line-height: ${lhM};
  letter-spacing: 0.4px;
  color: ${primaryColor};
  margin-top: 10px;
  margin-bottom: 38px;
`;

const MobileOnly = styled.div`
  flex-shrink: 0;
  ${mobileOnlyCss};
`;

const TabletOnly = styled.div`
  // for some reason IE shrinks this wrapper div and the related questions overflow without the flex-shrink: 0;
  flex-shrink: 0;
  ${minTabletOnlyCss};
`;

const getPrevSection = (
  activeSection: HelpCenterSection,
  labels: {
    contact_form_url: string;
    contact_form_label: string;
  },
  formState: Omit<FormDataRaw, 'attachments'> | null,
) => {
  if (formState === null || formState.subject === null) {
    return {
      label: activeSection.name,
      url: createHelpCenterSectionUrl(activeSection),
    };
  }

  return {
    label: labels.contact_form_label,
    url: labels.contact_form_url,
  };
};

const HelpdeskArticlePageUI: React.FC<Props> = ({
  locale,
  sections,
  translations,
  relatedArticles,
  activeArticle,
  activeSectionId,
  endpoints,
  isShowOldHelpCenterEnabled,
  CloudflareTurnstile,
  isDesktop,
}) => {
  const activeSection = sections.find(
    section => section.id === activeSectionId,
  );

  const relatedArticlesComponent = (
    <RelatedArticles
      articles={relatedArticles}
      sections={sections}
      title={translations.articlePage.relatedQuestions}
    />
  );

  const articleUserFeedback = activeArticle ? (
    <ArticleUserFeedback
      translations={translations.articlePage.user_feedback}
      errorTranslation={translations.contactPage.form.feedback_error_text}
      onVote={(vote, captchaToken) =>
        endpoints.sendUserFeedback({
          articleId: activeArticle.id,
          captchaToken,
          vote,
        })
      }
      CloudflareTurnstile={CloudflareTurnstile}
    />
  ) : null;

  const formState = useContactFormState();
  // Using TrackingPageTypeWithoutId.HelpCenter as the article information is not necessary for this use case
  // https://aboutyou.atlassian.net/wiki/spaces/AYSA/pages/965515195/Help+Center+Tracking+Requirements#One-Time-AYSA-Analysis%3A-Check-Call-behavior-should-have
  const pageContext = getPageContextProps(TrackingPageTypeWithoutId.HelpCenter);

  return (
    <SharedPageTrackingContextProvider
      {...pageContext}
      type={TrackingPageTypeWithoutId.HelpCenter}
    >
      <HelpdeskInnerPage
        locale={locale}
        selectedSectionId={activeSectionId}
        sections={sections}
        translations={translations}
        endpoints={endpoints}
        contentBelowNavigation={
          <TabletOnly>{relatedArticlesComponent}</TabletOnly>
        }
        isShowOldHelpCenterEnabled={isShowOldHelpCenterEnabled}
      >
        {!activeArticle || !activeSection ? (
          <LoadingContainer />
        ) : (
          <>
            <BreadCrumbs
              crumbs={[
                {label: translations.breadcrumb.help, url: '/h'},
                getPrevSection(
                  activeSection,
                  {
                    contact_form_url: `/h/${specialSlugs(locale).contact}`,
                    contact_form_label: translations.footer.contact_form,
                  },
                  formState,
                ),
              ]}
              translations={translations.breadcrumb}
            />
            <ArticleContentWrapper>
              <Title data-testid="HelpCenterArticlePageTitle">
                {activeArticle.name}
              </Title>
              <ArticleContent activeArticle={activeArticle} locale={locale} />
              {isDesktop && <TabletOnly>{articleUserFeedback}</TabletOnly>}
            </ArticleContentWrapper>
          </>
        )}
        <MobileOnly>
          {!isDesktop && articleUserFeedback}
          {relatedArticlesComponent}
        </MobileOnly>
      </HelpdeskInnerPage>
    </SharedPageTrackingContextProvider>
  );
};

export default HelpdeskArticlePageUI;
